import first from "lodash/first";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import mapValues from "lodash/mapValues";
import omitBy from "lodash/omitBy";
import { parseValidationErrors } from "PFCore/helpers/api_errors/api_error_responses";
import { UnknownError } from "PFTypes";
import { useCallback } from "react";
import { useTranslation, UseTranslationOptions } from "react-i18next";

import { I18nNamespace } from "../i18n/i18n";
import { ErrorFormatOptions, getErrorMessage, getErrorName } from "./api_errors/response_errors_utils";

type UseResponseErrors = {
  translationNs?: I18nNamespace;
  translationOptions?: UseTranslationOptions<any>;
};

type BoundErrorUtils = {
  getAllErrorsByType: (
    apiErrorResponse: UnknownError,
    formatOptions?: ErrorFormatOptions
  ) => Record<string, string[]>;
  getSingleErrorByType: (
    apiErrorResponse: UnknownError,
    formatOptions?: ErrorFormatOptions
  ) => Record<string, string>;
  getFirstError: (apiErrorResponse: UnknownError) => string | undefined;
  isErrorUnauthorized: (errors: unknown, options?: ErrorFormatOptions) => boolean;
};

export const useResponseErrors = ({
  translationNs = "translation",
  translationOptions = {}
}: UseResponseErrors = {}): BoundErrorUtils => {
  const { t } = useTranslation(translationNs, translationOptions);

  const getAllErrorsByType = useCallback(
    (apiErrorResponse: UnknownError, formatOptions?: ErrorFormatOptions) => {
      const errorsVersion =
        apiErrorResponse?.response?.headers?.["x-api-version"] ||
        apiErrorResponse?.headers?.["x-api-version"];
      if (errorsVersion === "v1") {
        return (apiErrorResponse.response?.data?.errors ||
          apiErrorResponse.data?.errors ||
          apiErrorResponse.errors) as unknown as Record<string, string[]>;
      }

      const errors = parseValidationErrors(apiErrorResponse);

      if (isEmpty(errors)) {
        return {};
      }

      return mapValues(
        omitBy(
          groupBy(errors, (error) => getErrorName(error, formatOptions)),
          isEmpty
        ),
        (errors) => map(errors, (error) => getErrorMessage(t, error))
      );
    },
    [t]
  );

  const getSingleErrorByType = useCallback(
    (apiErrorResponse: UnknownError, formatOptions?: ErrorFormatOptions) =>
      mapValues(getAllErrorsByType(apiErrorResponse, formatOptions), first) as Record<string, string>,
    [getAllErrorsByType]
  );

  const getFirstError = useCallback(
    (apiErrorResponse: UnknownError) => {
      const errors = parseValidationErrors(apiErrorResponse);
      if (!errors) {
        return;
      }

      const firstError = first(errors);
      return firstError && getErrorMessage(t, firstError);
    },
    [t]
  );

  const isErrorUnauthorized = useCallback(
    (errors: UnknownError) => {
      const formattedErrors = getSingleErrorByType(errors);
      return Object.values(formattedErrors).flat().includes("Unauthorized");
    },
    [getSingleErrorByType]
  );

  return {
    getAllErrorsByType,
    getSingleErrorByType,
    getFirstError,
    isErrorUnauthorized
  };
};
